

import { useState, useEffect, useMemo, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import Badge from "../../components/Badge";
import Modal from "../../components/Modal";
import VendorForm from "../VendorForm";
import { vendorsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatCurrencyFull, formatDate } from "@/lib/data";
import {
  Calendar,
  AlertCircle,
  ExternalLink
} from "lucide-react";

interface Vendor {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  type?: string;
  vendor_type?: { id: number; name: string };
  commission_type?: string;
  commission_percent?: number;
  commission_value?: number;
  status?: string;
  balance?: number;
  gst_number?: string;
}

interface VendorAssignment {
  id: number;
  event_id: number;
  description: string;
  total_amount: number;
  paid_amount: number;
  balance: number;
  status?: string;
  event?: {
    name: string;
    start_date: string;
    end_date?: string;
    status?: string;
  } | null;
}

interface VendorPayment {
  id: number;
  amount: number;
  payment_date: string;
  paid_at?: string;
  payment_method?: string;
  transaction_id?: string;
  reference?: string;
  notes?: string;
  event?: {
    id: number;
    name: string;
  } | null;
}

interface VendorLedger {
  assignments: VendorAssignment[];
  payments: VendorPayment[];
  totalBilled: number;
  totalPaid: number;
  totalBalance: number;
}

function VendorDetailPageContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();

  // NOTE: Using query parameter (?id=) instead of dynamic route for static export compatibility
  const vendorId = searchParams.get("id");

  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [ledger, setLedger] = useState<VendorLedger | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    if (!vendorId) {
      setError("No vendor ID provided");
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [vendorRes, ledgerRes] = await Promise.all([
          vendorsApi.get(parseInt(vendorId)),
          vendorsApi.getLedger(parseInt(vendorId))
        ]);

        const vendorData = vendorRes.data?.data || vendorRes.data;

        if (!vendorData || !vendorData.id) {
          setError("Vendor not found");
          setVendor(null);
          return;
        }

        if (vendorData.id !== parseInt(vendorId)) {
          setError("Vendor ID mismatch");
          setVendor(null);
          return;
        }

        setVendor(vendorData);

        if (ledgerRes.success && ledgerRes.data) {
          setLedger(ledgerRes.data);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load vendor details";
        setError(message);
        showToast(message, "error");
        setVendor(null);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [vendorId, showToast]);

  // Calculate and categorize assignments
  const { upcomingPending, pastPending, completedAssignments, stats } = useMemo(() => {
    if (!ledger?.assignments) {
      return {
        upcomingPending: [],
        pastPending: [],
        completedAssignments: [],
        stats: {
          totalUpcomingPending: 0,
          totalPastPending: 0,
          totalPending: 0,
          upcomingCount: 0,
          pastCount: 0,
          completedCount: 0
        }
      };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcomingPending = ledger.assignments.filter(a => {
      const eventDate = a.event?.start_date ? new Date(a.event.start_date) : null;
      return eventDate && eventDate >= today && a.balance > 0;
    });

    const pastPending = ledger.assignments.filter(a => {
      const eventDate = a.event?.start_date ? new Date(a.event.start_date) : null;
      return eventDate && eventDate < today && a.balance > 0;
    });

    const completedAssignments = ledger.assignments.filter(a => a.balance <= 0);

    const totalUpcomingPending = upcomingPending.reduce((sum, a) => sum + a.balance, 0);
    const totalPastPending = pastPending.reduce((sum, a) => sum + a.balance, 0);

    return {
      upcomingPending,
      pastPending,
      completedAssignments,
      stats: {
        totalUpcomingPending,
        totalPastPending,
        totalPending: totalUpcomingPending + totalPastPending,
        upcomingCount: upcomingPending.length,
        pastCount: pastPending.length,
        completedCount: completedAssignments.length
      }
    };
  }, [ledger]);

  // Helper function to get status badge
  const getStatusBadge = (balance: number, eventDate?: string) => {
    if (balance <= 0) return { label: 'Paid', status: 'success' as const };

    if (eventDate) {
      const date = new Date(eventDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date < today) {
        return { label: 'Overdue', status: 'error' as const };
      }
    }
    return { label: 'Pending', status: 'warning' as const };
  };

  // Reload ledger after payment
  const reloadLedger = async () => {
    if (!vendorId) return;
    try {
      const ledgerRes = await vendorsApi.getLedger(parseInt(vendorId));
      if (ledgerRes.success && ledgerRes.data) {
        setLedger(ledgerRes.data);
      }
    } catch (error) {
      showToast("Failed to reload ledger", "error");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6 animate-pulse">
          <div className="h-8 bg-stone-200 rounded w-64" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-stone-200 rounded-xl" />
            ))}
          </div>
          <div className="h-64 bg-stone-200 rounded-xl" />
          <div className="h-64 bg-stone-200 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !vendor) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-600 font-medium mb-4">{error || "Vendor not found"}</p>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const vendorType = vendor.vendor_type?.name || vendor.type || "Vendor";

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="px-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
              {vendorType}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-stone-800">{vendor.name}</h1>
              <p className="text-sm text-stone-500 mt-1">Vendor Details & Payment Overview</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="px-4 py-2 rounded-xl border border-stone-200 text-stone-700 hover:bg-stone-100 transition-colors text-sm font-medium"
            >
              Edit Vendor
            </button>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 rounded-xl border border-stone-200 text-stone-700 hover:bg-stone-100 transition-colors text-sm font-medium"
            >
              Back
            </button>
          </div>
        </div>

        {upcomingPending.length > 0 && (
          <div className="bg-white rounded-xl border border-amber-200 overflow-hidden">
            <div className="p-4 bg-amber-50 border-b border-amber-100">
              <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Upcoming Events with Pending Payments ({upcomingPending.length})
              </h3>
              <p className="text-sm text-amber-600 mt-1">
                Total Pending: {formatCurrencyFull(stats.totalUpcomingPending)}
              </p>
            </div>
            <div className="divide-y divide-stone-100">
              {upcomingPending.map(assignment => (
                <div key={assignment.id} className="p-4 hover:bg-stone-50">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-stone-800">{assignment.event?.name || `Event #${assignment.event_id}`}</p>
                      <p className="text-xs text-stone-500">
                        {formatDate(assignment.event?.start_date || '')} • {assignment.description || 'No description'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-amber-600">{formatCurrencyFull(Math.max(0, assignment.balance))}</p>
                      <p className="text-xs text-stone-500">
                        of {formatCurrencyFull(assignment.total_amount)}
                      </p>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 transition-all"
                      style={{ width: `${assignment.total_amount > 0 ? (assignment.paid_amount / assignment.total_amount) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-stone-500">
                      Paid: {formatCurrencyFull(assignment.paid_amount)}
                    </span>
                    <Badge label="Pending" status="warning" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Overdue Payments */}
        {pastPending.length > 0 && (
          <div className="bg-white rounded-xl border border-rose-200 overflow-hidden">
            <div className="p-4 bg-rose-50 border-b border-rose-100">
              <h3 className="font-semibold text-rose-800 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Overdue Payments ({pastPending.length})
              </h3>
              <p className="text-sm text-rose-600 mt-1">
                Total Overdue: {formatCurrencyFull(stats.totalPastPending)}
              </p>
            </div>
            <div className="divide-y divide-stone-100">
              {pastPending.map(assignment => (
                <div key={assignment.id} className="p-4 hover:bg-stone-50">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-stone-800">{assignment.event?.name || `Event #${assignment.event_id}`}</p>
                      <p className="text-xs text-stone-500">
                        {formatDate(assignment.event?.start_date || '')} • {assignment.description || 'No description'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-rose-600">{formatCurrencyFull(Math.max(0, assignment.balance))}</p>
                      <p className="text-xs text-stone-500">
                        of {formatCurrencyFull(assignment.total_amount)}
                      </p>
                    </div>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 transition-all"
                      style={{ width: `${assignment.total_amount > 0 ? (assignment.paid_amount / assignment.total_amount) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-stone-500">
                      Paid: {formatCurrencyFull(assignment.paid_amount)}
                    </span>
                    <Badge label="Overdue" status="error" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* All Event Assignments */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="p-4 bg-stone-50 border-b border-stone-100">
            <h3 className="font-semibold text-stone-800">All Event Assignments ({ledger?.assignments?.length || 0})</h3>
          </div>
          {!ledger?.assignments || ledger.assignments.length === 0 ? (
            <div className="text-center py-12 text-stone-400">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No event assignments yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Event</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Description</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Total Amount</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Paid</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Remaining</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500">Status</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {ledger.assignments.map((assignment) => {
                    const status = getStatusBadge(assignment.balance, assignment.event?.start_date);
                    return (
                      <tr key={assignment.id} className="hover:bg-stone-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-stone-800">{assignment.event?.name || `Event #${assignment.event_id}`}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-stone-600">
                          {formatDate(assignment.event?.start_date || '')}
                        </td>
                        <td className="px-4 py-3 text-sm text-stone-600">
                          {assignment.description || '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-stone-800">
                          {formatCurrencyFull(assignment.total_amount)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-emerald-600">
                          {formatCurrencyFull(assignment.paid_amount)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-amber-700">
                          {formatCurrencyFull(Math.max(0, assignment.balance))}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge label={status.label} status={status.status} />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => navigate(`/events/detail?id=${assignment.event_id}`)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="View Event"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Vendor Address (if available) */}
        {vendor.address && (
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <h3 className="font-semibold text-stone-800 mb-3">Address</h3>
            <p className="text-sm text-stone-600">{vendor.address}</p>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && (
          <Modal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            title="Edit Vendor"
          >
            <VendorForm
              initialData={{
                name: vendor.name,
                vendor_type_id: vendor.vendor_type?.id?.toString() || "",
                contact_person: vendor.phone || "",
                phone: vendor.phone || "",
                email: vendor.email || "",
                address: vendor.address || "",
                gst_number: vendor.gst_number || "",
                commission_type: vendor.commission_type || "fixed",
                commission_value: vendor.commission_value?.toString() || "0",
                status: vendor.status || "active",
              }}
              onSubmit={async (data) => {
                try {
                  await vendorsApi.update(vendor.id, data);
                  showToast("Vendor updated successfully", "success");
                  setIsEditModalOpen(false);
                  // Reload vendor details
                  const response = await vendorsApi.get(vendor.id);
                  setVendor(response.data?.data || response.data);
                } catch (error: any) {
                  showToast(error?.message || "Failed to update vendor", "error");
                }
              }}
              onCancel={() => setIsEditModalOpen(false)}
            />
          </Modal>
        )}

      </div>
    </DashboardLayout>
  );
}

function LoadingFallback() {
  return (
    <DashboardLayout>
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-stone-200 rounded w-64" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-24 bg-stone-200 rounded-xl" />
          ))}
        </div>
        <div className="h-64 bg-stone-200 rounded-xl" />
        <div className="h-64 bg-stone-200 rounded-xl" />
      </div>
    </DashboardLayout>
  );
}

export default function VendorDetailPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <VendorDetailPageContent />
    </Suspense>
  );
}
